import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Box,
  CircularProgress,
  Skeleton,
  TextField,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useMutation, useQuery } from '@apollo/client';
import { GET_INTAKE_PUBLIC, SUBMIT_INTAKE } from '../graphql/queries';
import { IntakeStatus } from '../types/generated/graphql';
import logo from '../images/logo.png';
import SchemaFormRenderer from '../components/intake/SchemaFormRenderer';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

type IntakeState = 'loading' | 'ready' | 'submitting' | 'success' | 'error' | 'closed';

export default function IntakePage() {
  const { slug } = useParams<{ slug: string }>();

  const [state, setState] = useState<IntakeState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [rawText, setRawText] = useState<string>('');
  const [submitResult, setSubmitResult] = useState<{
    normalizedResult?: unknown;
    summary?: string;
  }>({});

  // Fetch intake public data
  const { data, loading } = useQuery(GET_INTAKE_PUBLIC, {
    variables: { slug: slug || '' },
    skip: !slug,
    onCompleted: (data) => {
      if (!data.intakePublic) {
        setState('error');
        setErrorMessage('Intake form not found');
        return;
      }

      if (
        data.intakePublic.status === IntakeStatus.Completed ||
        data.intakePublic.status === IntakeStatus.Cancelled
      ) {
        setState('closed');
        setErrorMessage(
          data.intakePublic.status === IntakeStatus.Completed
            ? 'This intake form has already been submitted.'
            : 'This intake form has been cancelled.'
        );
        return;
      }

      setState('ready');
    },
    onError: (error) => {
      setState('error');
      setErrorMessage(error.message || 'Failed to load intake form');
    },
  });
  const intakeData = data?.intakePublic;
  useDocumentTitle('Intake', intakeData?.title);

  // Submit mutation
  const [submitIntake] = useMutation(SUBMIT_INTAKE);

  // Update state based on loading
  React.useEffect(() => {
    if (!slug) {
      setState('error');
      setErrorMessage('Invalid intake URL');
    } else if (loading) {
      setState('loading');
    }
  }, [slug, loading]);

  const handleSubmit = async () => {
    if (!slug) return;

    setState('submitting');
    setErrorMessage('');

    try {
      const input: { structuredData?: unknown; rawText?: string; fileIds?: string[] } = {};

      if (Object.keys(formData).length > 0) {
        input.structuredData = formData;
      }

      if (rawText.trim()) {
        input.rawText = rawText.trim();
      }

      const { data: result } = await submitIntake({
        variables: { slug, input },
      });

      if (result.submitIntake.status === IntakeStatus.Completed) {
        setState('success');
        setSubmitResult({
          normalizedResult: result.submitIntake.normalizedResult,
          summary: result.submitIntake.summary,
        });
      } else {
        setState('error');
        setErrorMessage('Submission failed. Please try again.');
      }
    } catch (error: unknown) {
      console.error('Error submitting intake:', error);
      setState('error');
      setErrorMessage(
        error instanceof Error ? error.message : 'Failed to submit intake'
      );
    }
  };

  const handleFormDataChange = (newData: Record<string, unknown>) => {
    setFormData(newData);
  };

  // Render loading state
  if (state === 'loading') {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Card>
          <CardContent sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <img
                src={logo}
                alt="Planapus Logo"
                style={{ height: '50px', width: 'auto', marginBottom: '16px' }}
              />
            </Box>
            <Box sx={{ px: 2 }}>
              <Skeleton variant="text" height={40} />
              <Skeleton variant="rectangular" height={200} sx={{ my: 2 }} />
              <Skeleton variant="text" height={30} />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Render closed state
  if (state === 'closed') {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card>
          <CardContent sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <img
                src={logo}
                alt="Planapus Logo"
                style={{ height: '50px', width: 'auto', marginBottom: '16px' }}
              />
            </Box>

            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <ErrorIcon sx={{ fontSize: 60, color: 'warning.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Form Closed
              </Typography>
            </Box>

            <Alert severity="warning" sx={{ mb: 3 }}>
              {errorMessage}
            </Alert>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Render error state
  if (state === 'error') {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card>
          <CardContent sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <img
                src={logo}
                alt="Planapus Logo"
                style={{ height: '50px', width: 'auto', marginBottom: '16px' }}
              />
            </Box>

            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Something Went Wrong
              </Typography>
            </Box>

            <Alert severity="error" sx={{ mb: 3 }}>
              {errorMessage}
            </Alert>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Render success state
  if (state === 'success') {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card>
          <CardContent sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <img
                src={logo}
                alt="Planapus Logo"
                style={{ height: '50px', width: 'auto', marginBottom: '16px' }}
              />
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Thank You!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Your response has been submitted successfully.
              </Typography>
              {submitResult.summary && (
                <Alert severity="info" sx={{ mt: 3, textAlign: 'left' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Summary
                  </Typography>
                  <Typography variant="body2">{submitResult.summary}</Typography>
                </Alert>
              )}
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Render form (ready state)
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Card>
        <CardContent sx={{ py: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <img
              src={logo}
              alt="Planapus Logo"
              style={{ height: '40px', width: 'auto', marginBottom: '16px' }}
            />
          </Box>

          {/* Form Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              {intakeData?.title || 'Information Request'}
            </Typography>
            {intakeData?.description && (
              <Typography variant="body1" color="text.secondary">
                {intakeData.description}
              </Typography>
            )}
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Schema Form (if schema exists) */}
          {intakeData?.schema && (
            <Box sx={{ mb: 4 }}>
              <SchemaFormRenderer
                schema={intakeData.schema}
                value={formData}
                onChange={handleFormDataChange}
              />
            </Box>
          )}

          {/* Free-form text area (if enabled) */}
          {intakeData?.hasFreeFormMode && (
            <Box sx={{ mb: 4 }}>
              {intakeData?.schema && (
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Additional Information
                </Typography>
              )}
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Add any additional information or context here..."
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                variant="outlined"
              />
            </Box>
          )}

          {/* Submit Button */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={state === 'submitting'}
              startIcon={
                state === 'submitting' ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
              sx={{ minWidth: 200 }}
            >
              {state === 'submitting' ? 'Submitting...' : 'Submit'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}
